import { PlusOutlined } from '@ant-design/icons';
import type { GetProp, UploadFile, UploadProps } from 'antd';
import { App, Image, Upload } from 'antd';
import React, { useEffect, useState } from 'react';
import type { UploadedImage } from './service';
import { createImageUpload, isUploadAbortError } from './service';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
type ImageValidationFile = Pick<FileType, 'name' | 'size' | 'type'>;

export interface ImageUploadValueItem {
  id?: string | number;
  mediaId?: string | number;
  name?: string;
  src?: string;
  thumbUrl?: string;
  url?: string;
  visitUrl?: string;
}

export type ImageUploadValue =
  | string
  | ImageUploadValueItem
  | Array<string | ImageUploadValueItem>;

interface ImageUploadBaseProps {
  value?: ImageUploadValue;
  accept?: string;
  buttonText?: string;
  disabled?: boolean;
  maxCount?: number;
  onUploaded?: (image: UploadedImage) => void;
}

export type ImageUploadProps = ImageUploadBaseProps &
(
  | {
    valueMode?: 'url';
    onChange?: (value?: string | string[]) => void;
  }
  | {
    /** 保留输入中的媒体引用；新增直传图片仍返回稳定公开 URL。 */
    valueMode: 'reference';
    onChange?: (value?: ImageUploadValue) => void;
  }
);

type ImageUploadFile = UploadFile & {
  imageReference?: ImageUploadValueItem;
};

const IMAGE_MAX_SIZE = 10 * 1024 * 1024;
const SUPPORTED_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

const IMAGE_URL_KEYS = ['url', 'visitUrl', 'src', 'thumbUrl'] as const;

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error || '图片上传失败');
};

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const parsePossibleJsonValue = (value: string): unknown => {
  const trimmedValue = value.trim();
  if (!trimmedValue || !['{', '['].includes(trimmedValue[0])) {
    return value;
  }

  try {
    return JSON.parse(trimmedValue) as unknown;
  } catch {
    return value;
  }
};

const collectImageUrls = (value: unknown): string[] => {
  if (!value) {
    return [];
  }
  if (typeof value === 'string') {
    const parsedValue = parsePossibleJsonValue(value);
    if (parsedValue !== value) {
      return collectImageUrls(parsedValue);
    }
    return value.trim() ? [value.trim()] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap(collectImageUrls);
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const directUrl = IMAGE_URL_KEYS.map((key) => record[key]).find(
      (item): item is string =>
        typeof item === 'string' && Boolean(item.trim()),
    );
    if (directUrl) {
      return [directUrl.trim()];
    }
    return ['response', 'data', 'file', 'image'].flatMap((key) =>
      collectImageUrls(record[key]));
  }
  return [];
};

const normalizeMaxCount = (maxCount: number | undefined) =>
  Math.max(1, Math.floor(Number(maxCount || 1)));

const getFileNameFromUrl = (url: string, index: number) => {
  const path = url.split('?')[0];
  const fileName = path.split('/').filter(Boolean).pop();
  return fileName ? decodeURIComponent(fileName) : `图片${index + 1}`;
};

const createFileListFromValue = (value: unknown): ImageUploadFile[] =>
  Array.from(new Set(collectImageUrls(value))).map<UploadFile>(
    (url, index) => ({
      uid: `uploaded-${url}`,
      name: getFileNameFromUrl(url, index),
      status: 'done',
      url,
      thumbUrl: url,
    }),
  );

const createReferenceFileList = (value: unknown): ImageUploadFile[] => {
  const parsed =
    typeof value === 'string' ? parsePossibleJsonValue(value) : value;
  if (Array.isArray(parsed)) {
    return parsed.flatMap(createReferenceFileList);
  }
  if (parsed && typeof parsed === 'object' && 'mediaId' in parsed) {
    // 不把数字 ID 转成字符串掩盖精度损失；业务提交边界负责验证。
    const { mediaId } = parsed;
    if (typeof mediaId === 'string' || typeof mediaId === 'number') {
      const url = collectImageUrls(parsed)[0];
      return [
        {
          uid: `reference-${mediaId}`,
          name: url ? getFileNameFromUrl(url, 0) : '图片预览暂不可用',
          status: 'done',
          url,
          thumbUrl: url,
          imageReference: { mediaId, ...(url ? { url } : {}) },
        },
      ];
    }
  }
  return createFileListFromValue(parsed);
};

const getUploadedImageFromResponse = (
  response: UploadFile['response'],
): UploadedImage | undefined => {
  if (!response || typeof response !== 'object') {
    return undefined;
  }
  const result = response as Partial<UploadedImage>;
  return result.url ? (result as UploadedImage) : undefined;
};

const getUploadFileUrl = (file: UploadFile) =>
  file.url || file.thumbUrl || getUploadedImageFromResponse(file.response)?.url;

const serializeImageValue = (urls: string[], maxCount: number) => {
  const nextUrls = urls.filter(Boolean);
  if (nextUrls.length === 0) {
    return undefined;
  }
  return maxCount === 1 ? nextUrls[0] : nextUrls;
};

const isImageFile = (file: ImageValidationFile) =>
  (file.type
    ? SUPPORTED_IMAGE_MIME_TYPES.has(file.type.toLowerCase())
    : /\.(gif|jpe?g|png|webp)$/i.test(file.name));

export const getImageUploadValidationError = (file: ImageValidationFile) => {
  if (!isImageFile(file)) {
    return '仅支持 JPG、PNG、GIF 或 WebP 图片';
  }
  if (file.size > IMAGE_MAX_SIZE) {
    return '图片大小不能超过 10 MB';
  }
  return undefined;
};

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  valueMode,
  accept = '.jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp',
  buttonText = '上传图片',
  disabled = false,
  maxCount,
  onUploaded,
}) => {
  const { message } = App.useApp();
  const normalizedMaxCount = normalizeMaxCount(maxCount);
  const [fileList, setFileList] = useState<ImageUploadFile[]>(() =>
    (valueMode === 'reference'
      ? createReferenceFileList(value)
      : createFileListFromValue(value)));
  const [previewImage, setPreviewImage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    setFileList(
      valueMode === 'reference'
        ? createReferenceFileList(value)
        : createFileListFromValue(value),
    );
  }, [value, valueMode]);

  const handlePreview = async (file: UploadFile) => {
    let nextPreviewImage = file.url || (file.preview as string);
    if (!nextPreviewImage && file.originFileObj) {
      nextPreviewImage = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(nextPreviewImage);
    setPreviewOpen(true);
  };

  const handleBeforeUpload: UploadProps['beforeUpload'] = (file) => {
    const validationError = getImageUploadValidationError(file);
    if (validationError) {
      message.error(validationError);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleCustomRequest: UploadProps['customRequest'] = ({
    file,
    onError,
    onProgress,
    onSuccess,
  }) => {
    if (!(file instanceof File)) {
      onError?.(new Error('请选择有效的图片文件'));
      return;
    }

    const uploadTask = createImageUpload(file, {
      mimeType: file.type,
      onProgress: (percent) => onProgress?.({ percent }),
    });
    uploadTask.promise
      .then((result) => {
        onUploaded?.(result);
        onSuccess?.(result);
      })
      .catch((error) => {
        if (!isUploadAbortError(error)) {
          message.error(getErrorMessage(error));
          onError?.(error as Error);
        }
      });
    return { abort: uploadTask.abort };
  };

  const handleChange: UploadProps['onChange'] = (info) => {
    const nextFileList = info.fileList
      .slice(-normalizedMaxCount)
      .map<ImageUploadFile>((file) => {
      const url = getUploadFileUrl(file);
      if (!url) {
        return file;
      }
      return {
        ...file,
        name: file.name || getFileNameFromUrl(url, 0),
        status: file.status === 'error' ? 'error' : 'done',
        thumbUrl: file.thumbUrl || url,
        url,
      };
    });

    setFileList(nextFileList);

    if (info.file.status === 'uploading') {
      return;
    }

    if (valueMode === 'reference') {
      const nextValues = nextFileList
        .filter((file) => file.status === 'done')
        .map((file) => file.imageReference ?? getUploadFileUrl(file))
        .filter(
          (item): item is string | ImageUploadValueItem => item !== undefined,
        );
      const resolveValue = () => {
        if (nextValues.length === 0) {
          return undefined;
        }
        if (normalizedMaxCount === 1) {
          return nextValues[0];
        }
        return nextValues;
      };
      onChange?.(resolveValue());
      return;
    }

    const uploadedUrls = nextFileList
      .filter((file) => file.status === 'done')
      .map(getUploadFileUrl)
      .filter((url): url is string => Boolean(url));
    onChange?.(serializeImageValue(uploadedUrls, normalizedMaxCount));
  };

  const uploadButton = (
    <button
      type="button"
      disabled={disabled}
      style={{ border: 0, background: 'none' }}
    >
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{buttonText}</div>
    </button>
  );

  return (
    <>
      <Upload
        accept={accept}
        beforeUpload={handleBeforeUpload}
        customRequest={handleCustomRequest}
        disabled={disabled}
        fileList={fileList}
        listType="picture-card"
        maxCount={normalizedMaxCount}
        multiple={normalizedMaxCount > 1}
        onChange={handleChange}
        onPreview={handlePreview}
      >
        {fileList.length >= normalizedMaxCount ? null : uploadButton}
      </Upload>
      {previewImage ? (
        <Image
          alt="图片预览"
          styles={{ root: { display: 'none' } }}
          preview={{
            open: previewOpen,
            onOpenChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => {
              if (!visible) {
                setPreviewImage('');
              }
            },
          }}
          src={previewImage}
        />
      ) : null}
    </>
  );
};

export default ImageUpload;
